import { useCallback } from 'react'
import type { ChainSelectionExtendPayload } from '@/components/app/shapes/shape-text-editor/hooks/use-shape-text-selection'
import { chainFrameTextLengths } from '@/components/app/shapes/shape-text-frame-registry'
import {
  computeChainSelectionSubRanges,
  frameLocalToGlobalOffset,
} from '@/lib/text-linking/locate-chain-caret-destination'
import { measureShapeParagraphsPlainTextLength } from '@/lib/text-linking/measure-paragraphs-plain-text-length'
import { getShapeById } from '../helpers/shape-list'
import { resolveChainFocusOffset } from '../helpers/chain-focus'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useChainTextSelection(ctx: ShapeManagerStateApi) {
  const { chainFrameIdsByShapeIdRef, chainTextSelectionRef, setChainTextSelection, shapesRef } = ctx

  const clearChainTextSelection = useCallback(() => {
    chainTextSelectionRef.current = null
    setChainTextSelection(null)
  }, [chainTextSelectionRef, setChainTextSelection])

  /**
   * Re-resolve a surviving cross-frame selection against the chain's CURRENT text distribution.
   *
   * Chain-global offsets are what the selection stores, and a reflow only redistributes characters
   * between frames — so the offsets still name the same text and only `frameLengths` goes stale.
   * Re-measuring them is therefore the whole of "the highlight follows the text across the seam".
   *
   * Measured from `shapesRef`, not from the registry: `updateShape` has already written the
   * post-reflow paragraphs there synchronously, whereas a frame's registered `getTextLength()`
   * reads its `textValue` React state and still reports the pre-reflow length until the next
   * render. Both count in the same `paragraphsToPlainText` space, so the two agree once settled.
   *
   * A reflow can shorten the story slightly — moving a whole paragraph to the next frame drops the
   * `
` that used to separate it from its neighbour — hence the clamp, and the collapse when the
   * clamp squeezes the range to nothing.
   */
  const reanchorChainTextSelection = useCallback(() => {
    const sel = chainTextSelectionRef.current

    if (!sel) return

    const frameLengths = sel.frameIds.map((id) =>
      measureShapeParagraphsPlainTextLength(getShapeById(shapesRef.current, id)?.paragraphs ?? [])
    )
    const total = frameLengths.reduce((sum, n) => sum + n, 0)
    const anchorGlobal = Math.max(0, Math.min(total, sel.anchorGlobal))
    const focusGlobal = Math.max(0, Math.min(total, sel.focusGlobal))

    if (anchorGlobal === focusGlobal) {
      clearChainTextSelection()

      return
    }

    const next = { frameIds: sel.frameIds, frameLengths, anchorGlobal, focusGlobal }

    chainTextSelectionRef.current = next
    setChainTextSelection(next)
  }, [clearChainTextSelection, chainTextSelectionRef, setChainTextSelection, shapesRef])

  /**
   * The live cross-frame selection resolved into per-frame local ranges, in flow order, or `null`
   * when the selection is an ordinary in-frame one. Every cross-chain text operation below routes
   * through this so there is exactly one place that decides "is this selection cross-frame".
   */
  const resolveChainSelectionParts = useCallback((): Array<{
    frameId: string
    start: number
    end: number
  }> | null => {
    // Re-measure before slicing. `frameLengths` is captured at drag start and deliberately never
    // re-measured DURING a drag (`use-shape-text-selection.ts`) — a mid-drag reflow would skew it.
    // But by the time anything resolves parts the drag is over, and a commit-time reflow may since
    // have moved characters across a seam, leaving the stored lengths one distribution behind and
    // every offset past the first frame wrong. Cutting on those deletes the wrong span in the
    // downstream frame (WMPCM-9396 Bug 3). Both consumers below — the clipboard read and the
    // mutation — are commit-time, so re-anchoring here is safe and, because it runs once per
    // resolve, guarantees a cut removes exactly the characters its own copy put on the clipboard.
    reanchorChainTextSelection()

    const sel = chainTextSelectionRef.current

    if (!sel) return null

    const parts = computeChainSelectionSubRanges(
      sel.frameLengths,
      sel.anchorGlobal,
      sel.focusGlobal
    )
      .map((sub) => ({ frameId: sel.frameIds[sub.frameIndex], start: sub.start, end: sub.end }))
      .filter((p): p is { frameId: string; start: number; end: number } => p.frameId != null)

    return parts.length > 0 ? parts : null
  }, [reanchorChainTextSelection, chainTextSelectionRef])

  /**
   * Shift+Arrow at a link boundary, or any shifted arrow while a cross-frame selection is live.
   *
   * Seeds a chain selection from the calling frame's own offsets the first time, then just walks
   * the focus one character at a time. Clamped to the story's ends; collapsing the range back to
   * the anchor drops it entirely so the ordinary in-frame selection takes over again.
   */
  const extendChainTextSelection = useCallback(
    (payload: ChainSelectionExtendPayload) => {
      const existing = chainTextSelectionRef.current
      const frameIds = existing?.frameIds ?? chainFrameIdsByShapeIdRef.current.get(payload.frameId)

      if (!frameIds || frameIds.length < 2) return

      const lengths = existing?.frameLengths ?? chainFrameTextLengths(frameIds)
      const myIndex = frameIds.indexOf(payload.frameId)

      if (myIndex < 0) return

      const total = lengths.reduce((sum, n) => sum + n, 0)
      const anchorGlobal =
        existing?.anchorGlobal ?? frameLocalToGlobalOffset(lengths, myIndex, payload.anchorLocal)
      const currentFocus =
        existing?.focusGlobal ?? frameLocalToGlobalOffset(lengths, myIndex, payload.focusLocal)
      const focusGlobal = Math.max(
        0,
        Math.min(total, resolveChainFocusOffset(frameIds, lengths, total, currentFocus, payload))
      )

      if (focusGlobal === anchorGlobal) {
        clearChainTextSelection()

        return
      }

      const next = { frameIds, frameLengths: lengths, anchorGlobal, focusGlobal }

      chainTextSelectionRef.current = next
      setChainTextSelection(next)
    },
    [
      clearChainTextSelection,
      chainFrameIdsByShapeIdRef,
      chainTextSelectionRef,
      setChainTextSelection,
    ]
  )

  /**
   * Select the whole story across an explicit set of chain members — Ctrl+A, seeded from outside
   * rather than grown from a drag.
   *
   * Replaces the old exit-edit-mode-then-object-select (WMPCM-9070 Ruling 2). That shape of
   * select-all ended the text session, so the Ctrl+X users press next fell through to the OBJECT
   * cut and deleted every frame in the chain instead of emptying it (WMPCM-9396 Bug 1).
   *
   * Ruling 2's actual purpose — a following Type-panel change fanning across the whole chain — is
   * preserved without the object selection: `applyShapeTextRunStyle` tests the cross-frame
   * selection BEFORE its editing-frame branch and styles each member's own slice. Cut, copy and
   * delete now get the same reach for free, which is the half that was missing.
   *
   * `frameIds` is EVERY member of the chain, locked and frozen included — the caller filters
   * neither (WMPCM-9728). A selection is a read over the story, so it spans the whole story;
   * protection is enforced at the write, in `mutateChainTextSelection`. Filtering here instead is
   * what left a locked member's text out of a chain copy and, on a two-frame chain, took the count
   * below the `< 2` guard below so Ctrl+A did nothing at all.
   *
   * Measured from `shapesRef` rather than `chainFrameTextLengths` for the reason
   * `reanchorChainTextSelection` documents: a frame's registered `getTextLength()` reads React
   * state and lags the model by a render.
   */
  const seedChainTextSelectionOverFrames = useCallback(
    (frameIds: string[]): boolean => {
      if (frameIds.length < 2) return false

      const frameLengths = frameIds.map((id) =>
        measureShapeParagraphsPlainTextLength(getShapeById(shapesRef.current, id)?.paragraphs ?? [])
      )
      const total = frameLengths.reduce((sum, n) => sum + n, 0)

      if (total <= 0) return false

      const next = { frameIds, frameLengths, anchorGlobal: 0, focusGlobal: total }

      chainTextSelectionRef.current = next
      setChainTextSelection(next)

      return true
    },
    [chainTextSelectionRef, setChainTextSelection, shapesRef]
  )

  return {
    clearChainTextSelection,
    reanchorChainTextSelection,
    resolveChainSelectionParts,
    extendChainTextSelection,
    seedChainTextSelectionOverFrames,
  }
}

export type ChainTextSelectionApi = ReturnType<typeof useChainTextSelection>
